import { addMonths, format, parseISO, startOfMonth, subMonths } from 'date-fns'
import { toast } from 'sonner'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { parseError } from '@/lib/errors'
import { useUpdateLeadPipelineFields } from '@/models/myPipeline'

const MONTHS_BACK = 3
const MONTHS_SHOWN = 16

function monthLabel(month: string): string {
  return format(parseISO(month), 'MMM yyyy')
}

/** A few past months through a year ahead, always including the deal's own month. */
function closeMonthOptions(current: string): string[] {
  const first = startOfMonth(subMonths(new Date(), MONTHS_BACK))
  const months = Array.from({ length: MONTHS_SHOWN }, (_, index) =>
    format(addMonths(first, index), 'yyyy-MM-dd')
  )
  return months.includes(current) ? months : [current, ...months].sort()
}

/** The month a deal is expected to close; changing it moves the deal to that month's pipeline. */
export function CloseMonthSelect({ leadId, month }: { leadId: string; month: string }) {
  const update = useUpdateLeadPipelineFields()

  const onChange = (value: string) => {
    update.mutate(
      { id: leadId, expectedCloseMonth: value },
      { onError: (error) => toast.error(parseError(error)) }
    )
  }

  return (
    <Select value={month} onValueChange={onChange} disabled={update.isPending}>
      <SelectTrigger size="sm" className="h-7 text-xs" aria-label="Expected close month">
        <SelectValue>{monthLabel(month)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {closeMonthOptions(month).map((option) => (
          <SelectItem key={option} value={option}>
            {monthLabel(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
